import { useState, useCallback, useRef, useEffect } from 'react';
import { unifiedApiService } from '../../services/unified';
import type { VideoUploadProgress } from '../../services/unified/types';

interface VideoUploadProps {
  applicationId: string;
  onUploadComplete: (videoId: string) => void;
  onUploadError: (error: string) => void;
  className?: string;
}

// Constants matching backend validation
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const MIN_DURATION_SECONDS = 5 * 60; // 5 minutes
const MAX_DURATION_SECONDS = 15 * 60; // 15 minutes
const SUPPORTED_FORMATS = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
const SUPPORTED_EXTENSIONS = ['.mp4', '.mov', '.avi', '.webm'];

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

// Helper function to format duration
const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Helper function to get video duration
const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(Math.floor(video.duration));
    };

    video.onerror = () => {
      reject(new Error('Failed to load video metadata'));
    };

    video.src = URL.createObjectURL(file);
  });
};

export function VideoUpload({
  applicationId,
  onUploadComplete,
  onUploadError,
  className = '',
}: VideoUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<VideoUploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const validateFile = async (file: File): Promise<{ valid: boolean; error?: string; duration?: number }> => {
    // Check file type
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      return {
        valid: false,
        error: `Unsupported video format. Please use ${SUPPORTED_EXTENSIONS.join(', ')}`,
      };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds ${formatFileSize(MAX_FILE_SIZE)}. Your file is ${formatFileSize(file.size)}`,
      };
    }

    // Get video duration
    try {
      const duration = await getVideoDuration(file);

      // Check duration
      if (duration < MIN_DURATION_SECONDS) {
        return {
          valid: false,
          error: `Video is too short. Minimum duration is ${formatDuration(MIN_DURATION_SECONDS)} (${formatDuration(duration)} provided)`,
        };
      }

      if (duration > MAX_DURATION_SECONDS) {
        return {
          valid: false,
          error: `Video is too long. Maximum duration is ${formatDuration(MAX_DURATION_SECONDS)} (${formatDuration(duration)} provided)`,
        };
      }

      return { valid: true, duration };
    } catch (err) {
      return {
        valid: false,
        error: 'Failed to read video metadata. Please ensure the file is a valid video.',
      };
    }
  };

  const handleFileUpload = useCallback(
    async (file: File, isRetry: boolean = false) => {
      setError(null);
      setIsUploading(true);
      setUploadProgress(null);

      try {
        // Validate file
        const validation = await validateFile(file);
        if (!validation.valid) {
          throw new Error(validation.error);
        }

        setSelectedFile(file);
        setVideoDuration(validation.duration || null);

        // Create abort controller for cancellation
        abortControllerRef.current = new AbortController();

        // Create FormData
        const formData = new FormData();
        formData.append('video', file);
        formData.append('duration', validation.duration!.toString());

        // Upload video with progress tracking
        const httpClient = unifiedApiService.getHttpClient().getInstance();
        const response = await httpClient.post(
          `/api/applications/${applicationId}/video`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            signal: abortControllerRef.current.signal,
            onUploadProgress: (progressEvent: any) => {
              if (progressEvent.total) {
                const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress({
                  percentage,
                  loaded: progressEvent.loaded,
                  total: progressEvent.total,
                });
              }
            },
          }
        );

        onUploadComplete(response.data.id);
        setRetryCount(0);
      } catch (err: any) {
        // Handle abort
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          setError('Upload cancelled');
          return;
        }

        const errorMessage = err.response?.data?.message || err.message || 'Failed to upload video';
        setError(errorMessage);
        onUploadError(errorMessage);

        // Don't increment retry count if this was already a retry
        if (!isRetry) {
          setRetryCount((prev) => prev + 1);
        }
      } finally {
        setIsUploading(false);
        setUploadProgress(null);
        abortControllerRef.current = null;

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [applicationId, onUploadComplete, onUploadError]
  );

  const handleRetry = useCallback(() => {
    if (selectedFile) {
      handleFileUpload(selectedFile, true);
    }
  }, [selectedFile, handleFileUpload]);

  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsUploading(false);
    setUploadProgress(null);
    setError('Upload cancelled');
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload]
  );

  const handleBrowseClick = () => {
    if (!isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200
          ${isDragOver ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'}
          ${isUploading ? 'pointer-events-none opacity-75' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={SUPPORTED_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          disabled={isUploading}
        />

        {isUploading ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Uploading video...</p>
              {uploadProgress && (
                <div className="mt-2">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress.percentage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {uploadProgress.percentage}% ({formatFileSize(uploadProgress.loaded)} /{' '}
                    {formatFileSize(uploadProgress.total)})
                  </p>
                </div>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
                className="mt-3 text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Cancel Upload
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center">
              <svg
                className="h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">
                Drop your demo video here, or{' '}
                <span className="text-primary-600 hover:text-primary-500">browse</span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                MP4, MOV, AVI, WebM • 5-15 minutes • Max {formatFileSize(MAX_FILE_SIZE)}
              </p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-red-800">Upload Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              {retryCount > 0 && selectedFile && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRetry();
                  }}
                  className="mt-2 text-sm text-red-600 hover:text-red-700 font-medium underline"
                >
                  Retry Upload
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedFile && videoDuration && !isUploading && !error && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">Video Uploaded Successfully</p>
              <p className="text-sm text-green-700 mt-1">
                {selectedFile.name} • {formatDuration(videoDuration)} • {formatFileSize(selectedFile.size)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
